/* @flow */

import { logError } from './utils/logger';
import { isUndefined, isHTMLIFrameElement, extend } from './utils/coreutils';
import {
  createIframe,
  cleanFrame,
  setIfrHeight,
  getIfrDoc
} from './utils/iframeutils';
import { addEvent } from './utils/browserutil';

/**
 * @description Implements adcontainer functionality
 * @exports adcontainer
 * @requires  module:utils
 * @requires module:logger
 */
class _adContainer {
  _iframeHolder: string;

  _sWrapper: string;

  constructor() {
    this._iframeHolder = '';
    this._sWrapper = '';
  }

  /**
   * init
   *
   * @param {module:globalParams.globalParams} g - Global param object
   * @returns {undefined}
   */
  init(g: globalParam) {
    this._iframeHolder = g.iframeHolder;
    this._sWrapper = g.sWrapper;
  }

  /**
   * create Ad Iframe
   *
   * @param {string} adUrl - static template url for loading ad iframe
   * @param {HTMLElement} adDivObj - ad div obj
   * @param {module:adloader.adInfo} adInfoObj - ad info
   * @returns {HTMLElement} -Ad IFrame Element
   * @private
   */
  _createAdIframe(
    adUrl: string,
    adDivObj: HTMLElement,
    adInfoObj: adInfo
  ): HTMLIFrameElement {
    const doc = adDivObj.ownerDocument;
    const { as, fID, sandbox } = adInfoObj;

    // clean Ad frame and container before loading new AD
    cleanFrame(doc, adDivObj, fID);

    // create iframe and set iframe onload handlers
    const newIF = createIframe(
      doc,
      fID,
      as.width,
      as.height,
      adUrl,
      isUndefined(as.sandbox) ? sandbox : as.sandbox
    );

    // attach onload handler for setting iframe height
    if (as.dynHeight !== undefined && as.dynHeight === true) {
      addEvent(newIF, 'load', () => setIfrHeight(fID));
    }

    // set margin height
    if (as.MarginHeight !== undefined) {
      newIF.marginHeight = String(as.MarginHeight);
    }

    // insert new iframe
    adDivObj.appendChild(newIF);
    return newIF;
  }

  /**
   * getter for ad container info
   *
   * @param {module:adloader.adInfo} adInfoObj - Ad Info object
   * @param {boolean} bActive - Active/inactive
   * @returns {undefined}
   */
  // eslint-disable-next-line no-unused-vars
  _updateAdContainerInfo(adInfoObj: adInfo, bActive: boolean = false): adInfo {
    const ret = extend(true, {}, adInfoObj);
    ret.fID = adInfoObj.id;
    ret.cID = adInfoObj.id + this._sWrapper;
    return ret;
  }

  /**
   * getter for div object:
   * handles applications which loads ads inside iframeHolder
   *
   * @param {string} adDivID - div ID
   * @returns {HTMLElement} -Ad IFrame Element
   * @private
   */
  _getAdDiv(adDivID: string): null | HTMLElement {
    let adDivObj = document.getElementById(adDivID);
    if (adDivObj) {
      return adDivObj;
    }
    if (!this._iframeHolder.length) {
      return adDivObj;
    }
    const iframeHolder = document.getElementById(this._iframeHolder);
    if (!isHTMLIFrameElement(iframeHolder)) {
      return adDivObj;
    }

    // $FlowFixMe
    const d = getIfrDoc(iframeHolder);
    if (d) {
      adDivObj = d.getElementById(adDivID);
    }
    return adDivObj;
  }

  /**
   * loader iframe AD
   *
   * @param {string} adUrl - static template url for loading ad iframe
   * @param {module:adloader.adInfo} adInfoObj - ad info
   * @returns {HTMLIFrameElement} - New Ad IFrame
   * @private
   */
  loadIframeAd(adUrl: string, adInfoObj: adInfo): ?HTMLIFrameElement {
    const adinfo = this._updateAdContainerInfo(adInfoObj);
    const adDivObj = this._getAdDiv(adinfo.cID);
    if (!adDivObj) {
      logError(`Container not found:${adinfo.cID}`);
      return;
    }
    // eslint-disable-next-line consistent-return
    return this._createAdIframe(adUrl, adDivObj, adinfo);
  }

  /**
   * loading non iframe AD
   *
   * @param {string} adPos - ad position
   * @param {string} data - ad content
   * @returns {undefined}
   * @private
   */
  loadNonIframeAd(adPos: string, data: string) {
    const adDivObj = this._getAdDiv(adPos + this._sWrapper);
    if (adDivObj) {
      adDivObj.innerHTML = data;
    }
  }

  // eslint-disable-next-line no-unused-vars, no-empty-function
  addContainerDivs(adPos: string = '') {}
}

// export singleton object as default for this module
const adcontainer: _adContainer = new _adContainer();
export { adcontainer, _adContainer };
